import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemIcon,
  MenuItem,
  Select,
  Typography,
  LinearProgress,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { LabelWithControl } from "components/common/LabelControl";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { amendReasonData } from "data/data";
import { useDispatch, useSelector } from "react-redux";
import { showNotificationDialog } from "redux/actions/notificationActions";
import CSVFileValidator from "csv-file-validator";
import { SOMS_API_KEY, UPLOAD_FAILED, UPLOAD_SUCCESS } from "services/CONSTANTS";
import { uploadAmendmentFile } from "services/amendCustomerOrdersService";
import { UPLOAD_FILE_PREFIX } from "components/common/CONSTANTS";
import dayjs from "dayjs";
import { showErrorMessageByCode } from "services/commonServices";
import { encryptString } from "components/common/EncryptDecrypt";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

export const UploadCustomerOrders = () => {
  const [amendReason, setAmendReason] = useState("");
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [errors, setErrors] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [showFileUploadPopup, setShowFileUploadPopup] = useState(false);
  const [amendReasonsList, setAmendReasonsList] = useState([]);
  const dispatch = useDispatch();
  const userType = useSelector((state) => state.user);
  const loginResponse = userType.loginResponse;
  const { userDetailResponse } = userType.userType;
  let usersResponse = userDetailResponse.channelList
  useEffect(() => {
    setAmendReasonsList(amendReasonData.filter(x => usersResponse.includes(x.reasonType)).sort((a, b) => (a.Reason > b.Reason) ? 1 : ((b.Reason > a.Reason) ? -1 : 0)))
  }, [usersResponse])

  const onDrop = useCallback(
    (files, fileRejections) => {
      if (!files.length) {
        const showErrorMessage = fileRejections.flatMap(x => x.errors).map(x => x.message).join(",")
        setFileError(showErrorMessage)
      } else {
        setFileError(null)
      }

      setShowFileUploadPopup(true);

      setAcceptedFiles([...acceptedFiles, ...files]);
      setShowFileUploadPopup(false);
    },
    [acceptedFiles]
  );

  function fileValidator(file) {
    if (!file.name.toLowerCase().includes(UPLOAD_FILE_PREFIX.toLowerCase()) || file.name.split(".").length > 2) {
      return {
        code: "file-name-format",
        message: `File name does not have correct prefix. Please add ThirdpartyOrderAmendments as prefix.`
      };
    }

    return null;
  }

  const {
    fileRejections,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    validator: fileValidator,
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
    disabled: acceptedFiles.length,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const removeFile = (file) => () => {
    const files = [...acceptedFiles];
    files.splice(files.indexOf(file), 1);
    setAcceptedFiles(files);
  };

  const showAcceptedFiles = acceptedFiles.map((file) => (
    <ListItem key={file.path} sx={{ pl: 0 }} dense={true} disablePadding={true}>
      <ListItemIcon sx={{ minWidth: "auto", pr: 1 }}>
        <DescriptionOutlinedIcon />
      </ListItemIcon>
      {file.path}
      <Button component={Link} onClick={removeFile(file)} color="primary">
        Remove
      </Button>
    </ListItem>
  ));

  const fileRejectionItems = fileRejections.map(({ file }) => (
    <>
      <ListItem
        key={file.path}
        sx={{ pl: 0 }}
        dense={true}
        disablePadding={true}
      >
        <ListItemIcon sx={{ minWidth: "auto", pr: 1, color: "red" }}>
          <DescriptionOutlinedIcon />
        </ListItemIcon>
        {file.path}
      </ListItem>
    </>
  ));

  const validate = () => {
    const errors = {};
    if (!acceptedFiles.length) {
      errors.acceptedFilesText = "Please select a file to proceed";
      setFileError("Please select a valid file to proceed")
    }

    if (!amendReason) {
      errors.amendReasonText = "Please select an amend/cancel reason";
    }

    setErrors(errors);

    return !Object.keys(errors).length;
  };

  function isAlphanumeric(str) {
    return /^'?[a-zA-Z0-9-_]+$/.test(str.trim());
  }

  function isNumeric(str) {
    return /^\d+$/.test(str.trim());
  }

  function isDate(str) {
    return dayjs(str.trim(), 'YYYYMMDD').isValid()
  }

  const handleUploadFile = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const config = {
      headers: [
        {
          name: "Order ID",
          inputName: "Order ID",
          required: false,
          validate: function (value) {
            return !value.trim() ? true : isAlphanumeric(value)
          },
        },
        {
          name: "Customer Order Date",
          inputName: "Customer Order Date",
          required: false,
          validate: function (value) {
            return !value.trim() ? true : isDate(value)
          },
        },
        {
          name: "Store ID",
          inputName: "Store ID",
          required: true,
          validate: function (value) {
            return isNumeric(value)
          },
        },
        {
          name: "Delivery Date",
          inputName: "Delivery Date",
          required: false,
        },
        {
          name: "PIN",
          inputName: "PIN",
          required: true,
          validate: function (value) {
            return isNumeric(value)
          },
        },
        {
          name: "Pack Size",
          inputName: "Pack Size",
          required: false,
          validate: function (value) {
            return !value.trim() ? true : isNumeric(value)
          },
        },
        {
          name: "Quantity Ordered in Units",
          inputName: "Quantity Ordered in Units",
          required: false,
          validate: function (value) {
            return !value ? true : !isNaN(value)
          },
        },
        {
          name: "Requested Quantity in Units",
          inputName: "Requested Quantity in Units",
          required: false,
          validate: function (value) {
            return !value.trim() ? true : !isNaN(value)

          },
        },
      ],
    };

    CSVFileValidator(acceptedFiles[0], config)
      .then(async(csvData) => {
        const { inValidData } = csvData;

        if (inValidData.length) {
          const errorList = inValidData.map(x => {
            if (x.message.includes("name should be")) return "Please check header sequence or header text."

            return "File content is not as expected. Please add proper values in the file.";
          })

          const uniqueErrorList = [...new Set(errorList)]

          const errorListItems = uniqueErrorList.map((x, index) => <ListItem dense sx={{ p: 0 }} key={index}>{`${index + 1}. ${x}`}</ListItem>);
          const multipleErrorsMsg = (
            <>
              <Typography variant="span">Please check the following issues in the file:</Typography>
              <List dense>{errorListItems}</List>
            </>
          );

          dispatch(
            showNotificationDialog(
              "Upload Failed",
              inValidData.length > 1 ? multipleErrorsMsg : errorList[0],
              'Done'
            )
          );
          return;
        }
        setShowFileUploadPopup(true);

        const encryptedEmailId = await encryptString(loginResponse?.email);
        const queryParams = {
          amendCancelReasons: amendReason,
          fileName: acceptedFiles[0].name,
        }

        const formData = new FormData();
        formData.append("file", acceptedFiles[0]);
        formData.append("userId", encryptedEmailId);

        const request = {
          apiKey: SOMS_API_KEY,
          params: formData,
          queryParams,
        }

        uploadAmendmentFile(request).then(response => {
          setShowFileUploadPopup(false);
          dispatch(showNotificationDialog("Upload Successful", UPLOAD_SUCCESS, 'Done'));
        }).catch(err => {
          const { errTitle, errContent } = showErrorMessageByCode(err)

          setShowFileUploadPopup(false);
          dispatch(showNotificationDialog(errTitle, errContent, 'Done'));
        });


      })
      .catch((err) => {
        setShowFileUploadPopup(false);
        console.error("error", err);
        dispatch(showNotificationDialog("Upload Failed", UPLOAD_FAILED, 'Done'));
      });
  };

  return (
    <>
      {showFileUploadPopup ? (
        <Dialog open={true} maxWidth="sm" fullWidth={true}>
          <DialogTitle>File Uploading</DialogTitle>
          <DialogContent>
            <LinearProgress />
            <Typography>
              To ensure the successful upload, please avoid clicking the back
              button, leaving the page, or refreshing it during the upload
              process
            </Typography>
          </DialogContent>
        </Dialog>
      ) : (
        <div></div>
      )}
      <div className="labelControl">
        <Grid
          container
          rowSpacing={2}
          columnSpacing={1}
          component="form"
          onSubmit={handleUploadFile}
          sx={{ "& .MuiFormHelperText-root": { ml: 0 } }}
        >
          <Grid container spacing={1} item>
            <Grid item xs={12}>
              <div {...getRootProps({ style })} className="uploadPageStyling">
                <input data-testid="dropzone" {...getInputProps()} />
                <FileUploadOutlinedIcon fontSize="large" />
                <span>Drag and drop to upload or browse for files</span>
                <Typography variant="body2">(Only csv files allowed)</Typography>
                {isDragReject && (
                  <Typography color="error.main">
                    Some file types are not supported
                  </Typography>
                )}
              </div>
              <Typography variant="caption" color="error.main">
                {!showAcceptedFiles.length ? fileError : ''}
              </Typography>
              {showAcceptedFiles && (
                <aside className="asideStyle">
                  <List sx={{ pt: 0.5 }} dense={true}>
                    {showAcceptedFiles} {fileRejectionItems}
                  </List>
                </aside>
              )}
            </Grid>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <LabelWithControl label="Amend / Cancel Reason">
              <FormControl fullWidth size="small" error={!amendReason ? errors?.amendReasonText : ""}>
                {!amendReason && (
                  <InputLabel
                    id="reason-label"
                    sx={{
                      "&.Mui-focused": { display: "none" },
                      "&.Mui-error": { color: "initial" },
                    }}
                    style={{ fontSize: "12px", top: "-4px" }}
                  >
                    Amend / Cancel Reason
                  </InputLabel>
                )}
                <Select
                  labelId="reason-label"
                  id="reason"
                  value={amendReason}
                  className="dropDownValue"
                  onChange={(e) => setAmendReason(e.target.value)}
                >
                  {amendReasonsList.map((x) => (
                    <MenuItem value={x.Id}>{x.Reason}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{!amendReason ? errors?.amendReasonText : "" || ""}</FormHelperText>
              </FormControl>
            </LabelWithControl>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Button
              variant="contained"
              type="submit"
              style={{ fontSize: "12px", marginTop: "17px", height: "25px", padding: "4px 10px" }}
            >
              Upload File
            </Button>
          </Grid>


        </Grid>
      </div>
    </>
  );
};
